<script setup>

import { inject, ref, computed, watch } from 'vue';
import Listbox from 'primevue/listbox';

const schema = inject('schema');
const getTypeObjet = inject('getTypeObjet');

const { affichee, activee, typeObjet, variables, texte, positionCurseur, evenement } = defineProps({
	affichee: Boolean,
    activee: Boolean,
    typeObjet: Object,
	variables: Array, 
    texte: String,
    positionCurseur: Number,
    evenement: Object
});

const emit = defineEmits(['validation', 'toucheEntree', 'update:affichee']);

const suggestionSelectionnee = ref(null);
const indicePage = ref(0);

const REGEX_MOT = /[a-zA-Z0-9_<>]/;
const regexSepExpressions = /^[(*\/^+\[ -]$/;
const nbSuggestionsParPage = 10;

const expressionCourante = computed(() => {
	if (!activee || positionCurseur === 0) {
		return null;
	}
	return getExpressionPrecedant(positionCurseur);
});

const suggestions = computed(() => {
	if (activee && expressionCourante.value) {
		let listeSuggestions = getSuggestionsExpression(expressionCourante.value, schema.value);
        if (listeSuggestions?.length === 1 && listeSuggestions[0] === expressionCourante.value.dernierMot) {
            return null;
        }
        return listeSuggestions;
	} else {
		return null;
	}
});

const indicePremiereSuggestionAffichee = computed(() => {
	return indicePage.value * nbSuggestionsParPage;
});

const indiceDerniereSuggestionAffichee = computed(() => {
	const indice = (indicePage.value + 1) * nbSuggestionsParPage - 1;
	if (indice < suggestions.value.length) {
		return indice;
	} else {
		return suggestions.value.length - 1;
	}
});

const suggestionsPage = computed(() => {
	if (suggestions.value) {
		return suggestions.value.slice(indicePremiereSuggestionAffichee.value, indiceDerniereSuggestionAffichee.value + 1);
	} else {
		return null;
	}
});

watch(() => suggestions.value, (liste) => {
	suggestionSelectionnee.value = null;
	indicePage.value = 0;
	const aff = (liste !== null);
	if (affichee !== aff) {
		emit('update:affichee', aff);
	}
});

watch(() => evenement.increment, (increment) => {
	if (!suggestions.value) {
		return;
	}
    if (increment) {
        delete evenement.increment;
        var indiceSelection = suggestions.value.indexOf(suggestionSelectionnee.value);
		if (indiceSelection === -1) {
			indiceSelection = (increment > 0 ? 0 : suggestions.value.length - 1);
		} else {
			indiceSelection += increment;
			if (indiceSelection < 0) {
				indiceSelection = -1;
			} else if (indiceSelection >= suggestions.value.length) {
				indiceSelection = -1;
			}
		}
		if (indiceSelection === -1) {
			suggestionSelectionnee.value = null;
			indicePage.value = 0;
		} else {
			suggestionSelectionnee.value = suggestions.value[indiceSelection];
			indicePage.value = Math.floor(indiceSelection / nbSuggestionsParPage);
		}
    }
});

watch(() => evenement.validation, (validation) => {
    if (validation) {
        delete evenement.validation;
        valider(suggestionSelectionnee.value);
    }
});

function valider(mot) {
	if (mot) {
		const infosCompletion = {
			expressionCourante: expressionCourante.value, 
			motComplete: mot
		};
		emit('validation', infosCompletion);
	} else {
		emit('toucheEntree');
	}
}

function getExpressionPrecedant(position) {
	var caractere = texte.charAt(position);
	if (REGEX_MOT.test(caractere)) {
		return null;
	}
	var mot = '';
	var estExpressionChainee = false;
	var positionCaractere = position - 1;
	while (positionCaractere >= 0) {
		caractere = texte.charAt(positionCaractere);
		if (REGEX_MOT.test(caractere)) {
			mot = caractere + mot;
		} else {
			if (caractere === '.') {
				estExpressionChainee = true;
			}
			break;
		}
		positionCaractere--;
	}
	var positionDebutMotCourant = positionCaractere + 1;
	if (mot.length === 0 && !estExpressionChainee) {
		while(positionCaractere > 0 && caractere === ' ') {
			positionCaractere--;
			caractere = texte.charAt(positionCaractere);
		}
		if (caractere && !regexSepExpressions.test(caractere)) {
			return null;
		}
	}
	var exprTypeObjet;
	if (!expressionCourante.value || estExpressionChainee || positionDebutMotCourant !== expressionCourante.value.positionDebutMotCourant) {
		if (estExpressionChainee) {
			exprTypeObjet = getExpressionTypeObjetPrecedant(positionDebutMotCourant - 1);
		} else {
			exprTypeObjet = {expression: '', typeObjet: typeObjet};
		}
	} else {
		exprTypeObjet = expressionCourante.value.exprTypeObjet;
	}
	if (exprTypeObjet) {
		return {dernierMot: mot, exprTypeObjet: exprTypeObjet, positionDebutMotCourant: positionDebutMotCourant};
	} else {
		return null;
	}
}

function getExpressionTypeObjetPrecedant(position) {
	const derniereExpressionChaine = getExpressionChainePrecedant(position);
	position -= derniereExpressionChaine.length + 1;
	var exprTypeObjet;
	if (position >= 0 && texte.charAt(position) === '.') {
		exprTypeObjet = getExpressionTypeObjetPrecedant(position);
	} else {
		exprTypeObjet = {expression: '', typeObjet: typeObjet};
	}
	if (!exprTypeObjet) {
		return null;
	}
	const nom = getNomExpression(derniereExpressionChaine);
	const typeObjetRecherche = schema.value.typesObjet.find(typeObj => typeObj.nom === nom);
	if (typeObjetRecherche) {
		return {expression: exprTypeObjet, typeObjet: typeObjetRecherche};
	}
	const champ = exprTypeObjet.typeObjet.champs.find(chp => chp.nom === nom);
	if (champ?.type === 'ChampLien') {
		return {expression: exprTypeObjet, typeObjet: getTypeObjet(champ.typeDonnee.id)};
	}
	const variable = variables.find(variable => variable.nom === nom);
	if (variable?.typeDonnee?.classe === 'OBJET') {
		return {expression: exprTypeObjet, typeObjet: getTypeObjet(variable.typeDonnee.id)};
	}
	return null;
}

function getExpressionChainePrecedant(position) {
	var profondeurParentheses = 0;
	var caractere;
	const regexParenthesesOuvrantes = /[([{]/;
	const regexParenthesesFermantes = /[)\]}]/;
	const regexDelimiteurExpression = /[ +*/.-]/;
	var expression = '';
	position--;
	while (position >= 0) {
		caractere = texte.charAt(position);
		if (regexParenthesesFermantes.test(caractere)) {
			profondeurParentheses++;
		} else if (regexParenthesesOuvrantes.test(caractere)) {
			if (profondeurParentheses === 0) {
				return expression;
			} else {
				profondeurParentheses--;
			}
		} else if (profondeurParentheses === 0 && regexDelimiteurExpression.test(caractere)) {
			return expression;
		}
		expression = caractere + expression;
		position--;
	}
	return expression;	
}

function getNomExpression(expression) {
	const regexDelimiteur = /^([a-zA-Z0-9_]+)([^a-zA-Z0-9_].*|$)/;
	return expression.replace(regexDelimiteur, '$1');
}

function getSuggestionsExpression(expressionCourante, schema) {
	if (!expressionCourante) {
		return null;
	}
	const suggestions = [];
	const motRecherche = expressionCourante.dernierMot.toLowerCase();
	suggestions.push(...expressionCourante.exprTypeObjet.typeObjet.champs.map(champ => champ.nom).filter(expr => expr.toLowerCase().includes(motRecherche)));
	if (expressionCourante.exprTypeObjet.expression.length === 0) {
		suggestions.push(...schema.typesObjet.map(typeObjet => typeObjet.typeOccurrence === 'UNE' ? typeObjet.nom : `<${typeObjet.nom}>`).filter(expr => expr.toLowerCase().includes(motRecherche)));
		suggestions.push(...schema.dimensions.map(dim => `<${dim.nom}>`).filter(expr => expr.toLowerCase().includes(motRecherche)));
		suggestions.push(...schema.parametres.map(param => param.nom).filter(expr => expr.toLowerCase().includes(motRecherche)));
		suggestions.push(...variables.map(variable => variable.nom).filter(expr => expr.toLowerCase().includes(motRecherche)));
	}
	suggestions.sort((a, b) => comparerSuggestions(a, b, motRecherche));
	return suggestions;
}

function comparerSuggestions(a, b, motRecherche) {
	if (a.startsWith(motRecherche)) {
		if (b.startsWith(motRecherche)) {
			return a.localeCompare(b, undefined, {sensitivity: 'base'});
		} else {
			return -1;
		}
	} else {
		if (b.startsWith(motRecherche)) {
			return 1;
		} else {
			return a.localeCompare(b, undefined, {sensitivity: 'base'});
		}
	}
}

</script>

<template>
    <div class="popup-autocompletion"
		v-if="suggestions?.length > 0">
		<Listbox class="liste-suggestions" 
			v-model="suggestionSelectionnee" 
			:options="suggestionsPage">
			<template #option="slotProps">
				<div 
					tabindex="0" 
					class="elt-auto-completion" 
					:class="{exacte: expressionCourante.dernierMot && slotProps.option.startsWith(expressionCourante.dernierMot.toLowerCase())}" 
					@mousedown.stop.prevent="valider(slotProps.option)">{{ slotProps.option }}</div>
			</template>
		</Listbox>
		<div class="compteur-page">
			{{ indicePremiereSuggestionAffichee + 1 }} à {{ indiceDerniereSuggestionAffichee + 1 }} sur {{ suggestions.length }}
		</div>
    </div>
</template>

<style scoped>

.popup-autocompletion {
	min-width: 50px;
	min-height: 50px;
	max-height: 50px;
    z-index: 1000;
}

.compteur-page {
	background-color: whitesmoke;
	text-align: center;
	font-style: italic;
	border-color: var(--couleur-bordure);
	padding: 2px 5px;
}

.elt-auto-completion {
	font-style: italic;
	color: lightgray;
}

.elt-auto-completion.exacte {
	font-style: normal;
	color: var(--couleur-primaire);
}

</style>